
import BackupDetailsDialog from '@/components/dialogs/backups/BackupDetailsDialog.vue';
import DeleteBackupDialog from '@/components/dialogs/backups/DeleteBackupDialog.vue';
import RestoreBackupDialog from '@/components/dialogs/backups/RestoreBackupDialog.vue';
import {
BackupDto,
BackupId,
BackupRepositoryInterface,
BackupSummaryDto,
RestoreBackupDto
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import { Website } from "@/services/Website/interfaces";
import { ServerInterface, ServerRepositoryInterface, ServerType } from "@/services/Server/Interfaces";

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    BackupDetailsDialog,
    RestoreBackupDialog,
    DeleteBackupDialog,
  },
  inject: [
    'backupRepository',
    'serverRepository'
  ],
})
export default class AllBackup extends Vue {
  public loading: boolean = false;
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;

  public backupRepository: BackupRepositoryInterface;
  public serverRepository: ServerRepositoryInterface;

  public backups: BackupSummaryDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Repository', value: 'repositoryId' },
    { text: 'Origin Server', value: 'repo.targetOriginServerId' },
    { text: 'Scope', value: 'scope' },
    { text: 'Website Group', value: 'task.websiteGroupId' },
    { text: 'Status', value: 'status' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public scopeLabels = {
    'account': 'Terminated Backup',
    'server': 'Server Backup',
  };

  public searchOptions = {};

  public deleteDialog: string = '';
  public deleteDialogData: BackupDto | null = null;

  private _searchServerId: string = '';
  private servers: ServerInterface[] = [];

  public async initialize(): Promise<void> {
    await  this.serverRepository.getServersOfType(ServerType.ORIGIN).then((servers) => {
      this.servers = servers;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });

    await this.search();
  }

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackups();
    } catch (err) {
     await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  get searchServerId(): string {
    return this._searchServerId;
  }

  set searchServerId(value: string) {
    this._searchServerId = value;
    this.currentPage = 1;

    this.search();
  }

  public async getBackups(): Promise<void> {
    const result = await this.backupRepository.searchBackups(
      (this._searchServerId ?? '') !== '' ? this._searchServerId : undefined,
      {
      page: this.currentPage,
      pageSize: this.itemsPerPage,
    });

    this.totalItems = result?.total ?? 0;
    this.backups = result?.data ?? [];
  }

  public async changePage(): Promise<void> {
    await this.search();
  }

  public async goToDetails(backupId: BackupId): Promise<void> {
    await this.$router.push({ name: 'backup_details', params: { backupId } });
  }

  public async showDeleteDialogFor(id: BackupId): Promise<void> {
    try {
      const backup = await this.backupRepository.getBackup(id);
      if (!backup) {
        return;
      }

      this.deleteDialog = 'delete-backup-dialog';
      this.deleteDialogData = backup;
    } catch (err) {
      await this.onError(err);
    }
  }

  public async goToCreateBackup(): Promise<void> {
    await this.$router.push({ name: 'task_create' });
  }

  public async deleteBackup(backup: BackupDto): Promise<void> {
    try {
      await this.backupRepository.deleteBackup(backup.id);

      this.$successDialog.show('Backup deleted successfully');

      await this.search();
    } catch (err) {
      await this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
